import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Avatar,
  Stack,
  Divider,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { getUserList, updateUserPermissions, updateUserFrontendPermissions } from '../../services/api';

// Available permissions list
const AVAILABLE_PERMISSIONS = [
  // Dashboard
  { id: 'dashboard:read', name: 'View Dashboard', category: 'Dashboard', description: 'Access to dashboard overview' },
  
  // Orders
  { id: 'orders:read', name: 'View Orders', category: 'Orders', description: 'View order list and details' },
  { id: 'orders:create', name: 'Create Orders', category: 'Orders', description: 'Create new orders' },
  { id: 'orders:write', name: 'Edit Orders', category: 'Orders', description: 'Modify existing orders' },
  { id: 'orders:delete', name: 'Delete Orders', category: 'Orders', description: 'Remove orders' },
  
  // Transfer
  { id: 'transfer:read', name: 'View Transfers', category: 'Transfer', description: 'View transfer list and details' },
  { id: 'transfer:write', name: 'Edit Transfers', category: 'Transfer', description: 'Modify existing transfers' },
  { id: 'transfer:delete', name: 'Delete Transfers', category: 'Transfer', description: 'Remove transfers' },
  
  // Users
  { id: 'users:delete', name: 'Delete Users', category: 'Users', description: 'Remove users from system' },
  
  // Admin
  { id: 'admin', name: 'Admin Access', category: 'Administration', description: 'Full administrator access to all features' }
];

// Frontend menu structure with permissions
const FRONTEND_MENUS = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    description: 'Main dashboard and overview',
    visibilityPermission: 'menu:dashboard',
    childPermissions: [
      { id: 'dashboard:view', name: 'Show Dashboard', description: 'View dashboard page' }
    ]
  },
  {
    id: 'inventory-import',
    name: 'Inventory Import',
    description: 'Import inventory orders',
    visibilityPermission: 'menu:inventory-import',
    childPermissions: [
      { id: 'inventory-import:create', name: 'Create Order', description: 'Create new import orders' },
      { id: 'inventory-import:edit', name: 'Edit Order', description: 'Modify existing orders' },
      { id: 'inventory-import:edit-serials', name: 'Edit Serials', description: 'Modify product serials' },
      { id: 'inventory-import:confirm', name: 'Confirm Order', description: 'Confirm import orders' }
    ]
  },
  {
    id: 'inventory-transfer',
    name: 'Inventory Transfer',
    description: 'Transfer management',
    visibilityPermission: 'menu:inventory-transfer',
    childPermissions: [
      { id: 'inventory-transfer:create', name: 'Create Transfer', description: 'Create new transfer orders' },
      { id: 'inventory-transfer:edit-products', name: 'Edit product transfer', description: 'Can edit products in transfer and qty' },
      { id: 'inventory-transfer:edit-serials', name: 'Edit product serials in transfer', description: 'Can add or remove product serials' },
      { id: 'inventory-transfer:confirm-transfer-data', name: 'Confirm transfer data', description: 'Can confirm transfer data products and serials' },
      { id: 'inventory-transfer:import-transfer-data', name: 'Confirm product on new client', description: 'Confirm & Import product on new client' },
    ]
  },
  {
    id: 'inventory-inspection',
    name: 'Inventory Count History',
    description: 'View historical inventory counts',
    visibilityPermission: 'menu:inventory-inspection',
    childPermissions: [
      { id: 'inventory-inspection:view-history', name: 'View Count History', description: 'View counting history' }
    ]
  },
  {
    id: 'report',
    name: 'Report',
    description: 'Reports and analytics',
    visibilityPermission: 'menu:report',
    childPermissions: [
      { id: 'report:view', name: 'View Reports', description: 'Access and view reports' }
    ]
  },
  {
    id: 'tag-timeline',
    name: 'Tag Timeline',
    description: 'Tag tracking timeline',
    visibilityPermission: 'menu:tag-timeline',
    childPermissions: [
      { id: 'tag-timeline:view', name: 'View Timeline', description: 'Access tag timeline' }
    ]
  },
  {
    id: 'user-roles-permissions',
    name: 'User, Roles & Permissions',
    description: 'User and permission management',
    visibilityPermission: 'menu:user-roles-permissions',
    childPermissions: [
      { id: 'user-roles-permissions:view', name: 'View Users', description: 'View user list' },
      { id: 'user-roles-permissions:manage', name: 'Manage Permissions', description: 'Manage user permissions' }
    ]
  }
];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formPermissions, setFormPermissions] = useState([]);
  const [frontendPermissions, setFrontendPermissions] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch users from API
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const response = await getUserList();
      if (response && response.data) {
        setUsers(response.data);
      }
    } catch (error) {
      setErrorMessage('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (user) => {
    setSelectedUser(user);
    setFormPermissions(user.permissions || []);
    setFrontendPermissions(user.frontendPermissions || []);
    setOpenDialog(true);
    setActiveTab(1); // Default to Frontend Permissions tab
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setFormPermissions([]);
    setFrontendPermissions([]);
    setActiveTab(0);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePermissionChange = (permissionId, checked) => {
    setFormPermissions(prev => 
      checked
        ? [...prev, permissionId]
        : prev.filter(p => p !== permissionId)
    );
  };

  const handleFrontendPermissionChange = (permissionId, checked, menu) => {
    setFrontendPermissions(prev => {
      if (checked) {
        const newPerms = [...prev, permissionId];
        // Automatically add menu visibility permission if not already present
        if (menu && !newPerms.includes(menu.visibilityPermission)) {
          newPerms.push(menu.visibilityPermission);
        }
        return newPerms;
      } else {
        // Remove the permission
        return prev.filter(p => p !== permissionId);
      }
    });
  };

  const handleMenuVisibilityToggle = (menu, checked) => {
    if (checked) {
      // Add menu visibility permission and all child permissions
      const childPermissionIds = menu.childPermissions.map(p => p.id);
      setFrontendPermissions(prev => {
        const newPerms = [...prev, menu.visibilityPermission];
        childPermissionIds.forEach(id => {
          if (!newPerms.includes(id)) {
            newPerms.push(id);
          }
        });
        return newPerms;
      });
    } else {
      // Remove menu visibility permission and all child permissions
      const childPermissionIds = menu.childPermissions.map(p => p.id);
      setFrontendPermissions(prev => 
        prev.filter(p => p !== menu.visibilityPermission && !childPermissionIds.includes(p))
      );
    }
  };

  const isMenuVisible = (menu) => {
    return frontendPermissions.includes(menu.visibilityPermission);
  };

  // Calculate parent checkbox state for frontend permissions
  const getMenuCheckboxState = (menu) => {
    const childPermissionIds = menu.childPermissions.map(p => p.id);
    const checkedChildren = childPermissionIds.filter(id => frontendPermissions.includes(id));
    const totalChildren = childPermissionIds.length;
    const checkedCount = checkedChildren.length;

    if (checkedCount === 0) {
      return { checked: false, indeterminate: false };
    } else if (checkedCount === totalChildren) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  };

  const handleSave = async () => {
    if (!selectedUser) return;

    try {
      // Since Backend tab is hidden, always save frontend permissions
      await updateUserFrontendPermissions(selectedUser._id, frontendPermissions);
      setSuccessMessage('Frontend permissions updated successfully!');
      
      // Update user in local state
      setUsers(prev => prev.map(user => 
        user._id === selectedUser._id 
          ? { ...user, frontendPermissions: frontendPermissions }
          : user
      ));
      
      setTimeout(() => {
        handleCloseDialog();
      }, 1500);
    } catch (error) {
      setErrorMessage('Failed to update permissions. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Group permissions by category
  const permissionsByCategory = AVAILABLE_PERMISSIONS.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {});

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          User Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Click on a user to manage their permissions
        </Typography>
      </Box>

      {/* Error Message */}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Users Table */}
      {!loading && (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Permissions</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user, index) => (
                  <motion.tr
                    key={user._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    component={TableRow}
                    sx={{
                      '&:hover': { backgroundColor: '#f8fafc', cursor: 'pointer' },
                      '&:last-child td': { borderBottom: 0 }
                    }}
                    onClick={() => handleRowClick(user)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#7c2d12', width: 40, height: 40 }}>
                          {user.name ? user.name.charAt(0).toUpperCase() : user.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {user.name || user.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{user.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack direction="column" spacing={1}>
                        {/* Backend Permissions */}
                        {user.permissions && user.permissions.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Backend:
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                              {user.permissions.map(permission => (
                                <Chip
                                  key={permission}
                                  label={permission}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#7c2d12',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    mb: 0.5
                                  }}
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}
                        
                        {/* Frontend Permissions */}
                        {user.frontendPermissions && user.frontendPermissions.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Frontend: {user.frontendPermissions.length} permissions
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                              {user.frontendPermissions.slice(0, 3).map(permission => (
                                <Chip
                                  key={permission}
                                  label={permission}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#3b82f6',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    mb: 0.5
                                  }}
                                />
                              ))}
                              {user.frontendPermissions.length > 3 && (
                                <Chip
                                  label={`+${user.frontendPermissions.length - 3}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#cbd5e1',
                                    color: '#475569',
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    mb: 0.5
                                  }}
                                />
                              )}
                            </Stack>
                          </Box>
                        )}
                        
                        {(!user.permissions || user.permissions.length === 0) && 
                         (!user.frontendPermissions || user.frontendPermissions.length === 0) && (
                          <Typography variant="caption" color="text.secondary">
                            No permissions
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={user.isActive ? <CheckCircleIcon /> : <CancelIcon />}
                        label={user.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={user.isActive ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(user.createdAt)}
                      </Typography>
                    </TableCell>
                  </motion.tr>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Permissions Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Manage Permissions - {selectedUser?.name || selectedUser?.username}
            </Typography>
          </Box>
        </DialogTitle>
        <Divider />
        
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Backend Permissions" sx={{ display: 'none' }} />
            <Tab label="Frontend Permissions" />
          </Tabs>
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          {successMessage && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              {successMessage}
            </Alert>
          )}

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {/* Backend Permissions Tab */}
          {activeTab === 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select the backend API permissions for this user:
              </Typography>
              <Stack spacing={2}>
                {Object.entries(permissionsByCategory).map(([category, permissions]) => (
                  <Box key={category}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      {category}
                    </Typography>
                    <Stack spacing={1}>
                      {permissions.map(permission => (
                        <FormControlLabel
                          key={permission.id}
                          control={
                            <Checkbox
                              checked={formPermissions.includes(permission.id)}
                              onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {permission.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {permission.description}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </Stack>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                ))}
              </Stack>
            </>
          )}

          {/* Frontend Permissions Tab */}
          {activeTab === 1 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Control menu visibility and feature access for this user:
              </Typography>
              <Stack spacing={3}>
                {FRONTEND_MENUS.map(menu => {
                  const checkboxState = getMenuCheckboxState(menu);
                  return (
                    <Box 
                      key={menu.id}
                      sx={{ 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 2,
                        backgroundColor: '#fafafa'
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checkboxState.checked}
                            indeterminate={checkboxState.indeterminate}
                            onChange={(e) => handleMenuVisibilityToggle(menu, e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {menu.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {menu.description}
                            </Typography>
                          </Box>
                        }
                      />
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                        Feature Permissions:
                      </Typography>
                      <Stack spacing={1} sx={{ pl: 2 }}>
                        {menu.childPermissions.map(permission => (
                          <FormControlLabel
                            key={permission.id}
                            control={
                              <Checkbox
                                checked={frontendPermissions.includes(permission.id)}
                                onChange={(e) => handleFrontendPermissionChange(permission.id, e.target.checked, menu)}
                                color="primary"
                                size="small"
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {permission.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {permission.description}
                                </Typography>
                              </Box>
                            }
                          />
                        ))}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              backgroundColor: '#7c2d12',
              '&:hover': { backgroundColor: '#9a3412' },
              borderRadius: 2
            }}
          >
            Save Permissions
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
